/**
 * /suprimentos/depositos — onde o material está, e como movê-lo.
 *
 * Bloco 6 do ADR-0061. O saldo era um número só para a empresa inteira:
 * o almoxarife contava o galpão de chapa, achava 40, o sistema dizia 52,
 * e as 12 estavam no carrinho de obra. Os dois números certos, e nenhum
 * jeito de provar.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Carregando, Vazio, VazioInline } from "./_shared/Estados";
import {
  FiAlertCircle,
  FiEdit2,
  FiMapPin,
  FiPlus,
  FiRefreshCw,
  FiRepeat,
  FiTrash2,
} from "react-icons/fi";

import ConfirmDialog from "../../components/ConfirmDialog";
import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  apagarDeposito,
  atualizarDeposito,
  criarDeposito,
  desativarDeposito,
  listarDepositos,
  listarProdutos,
  saldoPorDeposito,
  transferirEntreDepositos,
} from "../../services/estoque";
import { formatarQuantidade } from "../../utils/dinheiro";

import {
  Card,
  ErrorMsg,
  Field,
  Form,
  FormFooter,
  FormGrid,
  InlineHint,
  Input,
  Pill,
  PrimaryButton,
  IconButton,
  SecondaryButton,
  Select,
  Table,
  TableWrapper,
  Textarea,
  TruncCell,
} from "./styles";

const TIPOS = [
  ["ALMOXARIFADO", "Almoxarifado"],
  ["CHAPA", "Depósito de chapa"],
  ["OBRA", "Carrinho de obra"],
  ["EXPEDICAO", "Expedição"],
  ["TERCEIRO", "Em poder de terceiro"],
  ["OUTRO", "Outro"],
];

const FORM_VAZIO = {
  codigo: "",
  nome: "",
  tipo: "ALMOXARIFADO",
  ordem_picking: "100",
  observacao: "",
};

const TRANSF_VAZIA = {
  produto: "",
  quantidade: "",
  origem: "",
  destino: "",
  observacao: "",
};

function Depositos() {
  const { request, isMaster, hasAllPermissions, user } = useAuth();
  const toast = useToast();
  const [apagando, setApagando] = useState(null);
  const [emUso, setEmUso] = useState(null);
  const [depositos, setDepositos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [novo, setNovo] = useState(null);
  const [transf, setTransf] = useState(null);
  const [saldos, setSaldos] = useState(null);

  // Bloco 6 (11/09/2026): deposito tem permissoes proprias.
  const podeCadastrar = isMaster || hasAllPermissions(["deposito:create"]);
  const podeEditar = isMaster || hasAllPermissions(["deposito:edit"]);
  const podeApagar = isMaster || hasAllPermissions(["deposito:delete"]);
  const podeTransferir = isMaster || hasAllPermissions(["estoque:transferir"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await listarDepositos(request);
      setDepositos(Array.isArray(payload) ? payload : payload?.results || []);
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar depósitos.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    listarProdutos(request, { pageSize: 500, ativo: true })
      .then((p) => setProdutos(Array.isArray(p) ? p : p?.results || []))
      .catch(() => setProdutos([]));
  }, [request]);

  const handleCriar = async (e) => {
    e.preventDefault();
    if (!novo.codigo || !novo.nome) {
      setErro("Código e nome são obrigatórios.");
      return;
    }
    setErro(null);
    try {
      const dados = {
        codigo: novo.codigo, nome: novo.nome, tipo: novo.tipo,
        ordem_picking: Number(novo.ordem_picking) || 100,
        observacao: novo.observacao || "",
      };
      if (novo.id) {
        await atualizarDeposito(request, novo.id, dados);
      } else {
        await criarDeposito(request, { ...dados, unidade: user?.business_unit });
      }
      setNovo(null);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao salvar depósito.");
    }
  };

  const handleDesativar = async (dep) => {
    setErro(null);
    try {
      await desativarDeposito(request, dep.id);
      toast.success(`Depósito ${dep.codigo} desativado.`);
      await carregar();
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Erro ao desativar depósito.");
    }
  };

  // Apagar de verdade; em uso, o backend responde 409 e a tela oferece desativar.
  const confirmarApagar = async () => {
    const dep = apagando;
    setApagando(null);
    try {
      await apagarDeposito(request, dep.id);
      toast.success(`Depósito ${dep.codigo} apagado.`);
      await carregar();
    } catch (err) {
      if (err?.status === 409 || err?.payload?.em_uso) {
        setEmUso({ ...dep, motivo: err?.payload?.detalhe });
        return;
      }
      toast.error(err?.payload?.detalhe || err?.message || "Erro ao apagar depósito.");
    }
  };

  const editar = (d) => setNovo({
    id: d.id, codigo: d.codigo, nome: d.nome, tipo: d.tipo,
    ordem_picking: String(d.ordem_picking ?? 100), observacao: d.observacao || "",
  });

  const handleTransferir = async (e) => {
    e.preventDefault();
    if (!transf.produto || !transf.quantidade || !transf.origem || !transf.destino) {
      setErro("Produto, quantidade, origem e destino são obrigatórios.");
      return;
    }
    setErro(null);
    try {
      const resp = await transferirEntreDepositos(request, {
        produto: transf.produto,
        quantidade: transf.quantidade,
        origem: transf.origem,
        destino: transf.destino,
        observacao: transf.observacao || "",
      });
      setTransf(null);
      // Mostrar onde o saldo ficou logo apos mover e o que fecha o
      // circuito: sem isso o almoxarife move e vai conferir noutra tela.
      setSaldos({
        produto: produtos.find((p) => p.id === transf.produto),
        linhas: resp?.saldo_por_deposito || [],
      });
    } catch (err) {
      setErro(err?.message || "Erro ao transferir.");
    }
  };

  const verSaldos = async (produtoId) => {
    setErro(null);
    try {
      const resp = await saldoPorDeposito(request, produtoId);
      setSaldos({
        produto: produtos.find((p) => p.id === produtoId),
        linhas: resp?.por_deposito || [],
        total: resp?.saldo_total,
      });
    } catch (err) {
      setErro(err?.message || "Erro ao consultar saldo.");
    }
  };

  return (
    <>
      <EntityHeader
        title="Depósitos"
        subtitle={`${depositos.length} cadastrado(s)`}
        primaryAction={
          podeTransferir
            ? {
                label: "Transferir material",
                icon: <FiRepeat size={14} />,
                onClick: () => setTransf({ ...TRANSF_VAZIA }),
              }
            : undefined
        }
        actions={[
          ...(podeCadastrar
            ? [{
                label: "Novo depósito",
                icon: <FiPlus size={14} />,
                onClick: () => setNovo({ ...FORM_VAZIO }),
              }]
            : []),
          { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
        ]}
      />

      <Card>
        <InlineHint>
          <FiMapPin size={13} /> A ordem de picking define por onde o
          almoxarife passa ao separar — do menor para o maior.
        </InlineHint>

        {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}

        {loading ? (
          <Carregando />
        ) : depositos.length === 0 ? (
          <Vazio titulo="Nenhum depósito cadastrado" />
        ) : (
          <TableWrapper>
            <Table>
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "30%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Picking</th>
                  <th>Situação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {depositos.map((d) => (
                  <tr key={d.id}>
                    <td>{d.codigo}</td>
                    <td title={d.nome}>
                      <TruncCell>
                        {d.nome}
                        {d.principal && (
                          <Pill $tone="info" style={{ marginLeft: 6, fontSize: "0.66rem" }}>
                            principal
                          </Pill>
                        )}
                      </TruncCell>
                    </td>
                    <td>{d.tipo_display || d.tipo}</td>
                    <td>{d.ordem_picking}</td>
                    <td>
                      <Pill $tone={d.ativo ? "success" : "neutral"}>
                        {d.ativo ? "Ativo" : "Inativo"}
                      </Pill>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        {podeEditar && (
                          <IconButton type="button" onClick={() => editar(d)} title="Editar depósito">
                            <FiEdit2 size={11} />
                          </IconButton>
                        )}
                        {podeEditar && !d.principal && (
                          d.ativo ? (
                            <SecondaryButton type="button" onClick={() => handleDesativar(d)}>Desativar</SecondaryButton>
                          ) : (
                            <SecondaryButton type="button" onClick={async () => { await atualizarDeposito(request, d.id, { ativo: true }); await carregar(); }}>Reativar</SecondaryButton>
                          )
                        )}
                        {podeApagar && !d.principal && (
                          <IconButton type="button" onClick={() => setApagando(d)} title="Apagar depósito">
                            <FiTrash2 size={11} />
                          </IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <Card>
        <h3 style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>
          Onde está o saldo
        </h3>
        <InlineHint>
          Escolha um material para ver em que depósito ele está.
        </InlineHint>
        <Select
          value=""
          onChange={(e) => e.target.value && verSaldos(e.target.value)}
          style={{ minWidth: 260, marginTop: 8 }}
        >
          <option value="">Selecione o material...</option>
          {produtos.map((p) => (
            <option key={p.id} value={p.id}>{p.codigo} — {p.nome}</option>
          ))}
        </Select>
      </Card>

      <SlideOverPanel
        open={Boolean(novo)}
        onClose={() => setNovo(null)}
        title={novo?.id ? `Editar depósito ${novo.codigo}` : "Novo depósito"}
      >
        {novo && (
          <Form onSubmit={handleCriar}>
            <FormGrid>
              <Field>
                <label>Código *</label>
                <Input
                  value={novo.codigo}
                  onChange={(e) => setNovo((p) => ({ ...p, codigo: e.target.value }))}
                  placeholder="CHAPAS"
                  maxLength={20}
                />
              </Field>
              <Field>
                <label>Nome *</label>
                <Input
                  value={novo.nome}
                  onChange={(e) => setNovo((p) => ({ ...p, nome: e.target.value }))}
                  placeholder="Galpão de chapas"
                  maxLength={80}
                />
              </Field>
              <Field>
                <label>Tipo</label>
                <Select
                  value={novo.tipo}
                  onChange={(e) => setNovo((p) => ({ ...p, tipo: e.target.value }))}
                >
                  {TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              </Field>
              <Field>
                <label>Ordem de picking</label>
                <Input
                  type="number"
                  value={novo.ordem_picking}
                  onChange={(e) => setNovo((p) => ({ ...p, ordem_picking: e.target.value }))}
                />
              </Field>
            </FormGrid>
            <Field>
              <label>Observação</label>
              <Textarea
                value={novo.observacao}
                onChange={(e) => setNovo((p) => ({ ...p, observacao: e.target.value }))}
                rows={2}
                maxLength={300}
              />
            </Field>
            {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setNovo(null)}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="submit">{novo.id ? "Salvar alterações" : "Criar depósito"}</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>

      <SlideOverPanel
        open={Boolean(transf)}
        onClose={() => setTransf(null)}
        title="Transferir material"
      >
        {transf && (
          <Form onSubmit={handleTransferir}>
            <InlineHint>
              Transferir não muda o saldo da empresa — só o lugar onde o
              material está.
            </InlineHint>
            <Field>
              <label>Material *</label>
              <Select
                value={transf.produto}
                onChange={(e) => setTransf((p) => ({ ...p, produto: e.target.value }))}
              >
                <option value="">Selecione...</option>
                {produtos.map((p) => (
                  <option key={p.id} value={p.id}>{p.codigo} — {p.nome}</option>
                ))}
              </Select>
            </Field>
            <FormGrid>
              <Field>
                <label>De *</label>
                <Select
                  value={transf.origem}
                  onChange={(e) => setTransf((p) => ({ ...p, origem: e.target.value }))}
                >
                  <option value="">Selecione...</option>
                  {depositos.filter((d) => d.ativo).map((d) => (
                    <option key={d.id} value={d.id}>{d.nome}</option>
                  ))}
                </Select>
              </Field>
              <Field>
                <label>Para *</label>
                <Select
                  value={transf.destino}
                  onChange={(e) => setTransf((p) => ({ ...p, destino: e.target.value }))}
                >
                  <option value="">Selecione...</option>
                  {depositos
                    .filter((d) => d.ativo && d.id !== transf.origem)
                    .map((d) => (
                      <option key={d.id} value={d.id}>{d.nome}</option>
                    ))}
                </Select>
              </Field>
            </FormGrid>
            <Field>
              <label>Quantidade *</label>
              <Input
                value={transf.quantidade}
                onChange={(e) => setTransf((p) => ({ ...p, quantidade: e.target.value }))}
                placeholder="0"
                inputMode="decimal"
              />
            </Field>
            <Field>
              <label>Observação</label>
              <Textarea
                value={transf.observacao}
                onChange={(e) => setTransf((p) => ({ ...p, observacao: e.target.value }))}
                rows={2}
                maxLength={300}
              />
            </Field>
            {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setTransf(null)}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="submit">Transferir</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>

      <SlideOverPanel
        open={Boolean(saldos)}
        onClose={() => setSaldos(null)}
        title={saldos?.produto
          ? `Saldo de ${saldos.produto.codigo}`
          : "Saldo por depósito"}
      >
        {saldos && (
          <div style={{ padding: "4px 0" }}>
            {saldos.total !== undefined && (
              <InlineHint>
                Total na empresa: <strong>{formatarQuantidade(saldos.total)}</strong>
              </InlineHint>
            )}
            {saldos.linhas.length === 0 ? (
              <VazioInline>Este material não tem saldo em nenhum depósito.</VazioInline>
            ) : (
              <TableWrapper style={{ marginTop: 10 }}>
                <Table>
                  <thead>
                    <tr>
                      <th>Depósito</th>
                      <th style={{ width: "35%" }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saldos.linhas.map((l) => (
                      <tr key={l.deposito_id}>
                        <td title={l.deposito_nome}>
                          <TruncCell>
                            {l.deposito_nome}
                            {l.principal && (
                              <Pill $tone="info" style={{ marginLeft: 6, fontSize: "0.66rem" }}>
                                principal
                              </Pill>
                            )}
                          </TruncCell>
                        </td>
                        <td>{formatarQuantidade(l.saldo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            )}
          </div>
        )}
      </SlideOverPanel>
      <ConfirmDialog
        open={Boolean(apagando)}
        titulo={`Apagar o depósito ${apagando?.codigo}?`}
        descricao="Só apaga se nada apontar para ele (movimentos, ajustes, inventários, boxes, remessas, configuração). Nesses casos você poderá desativar em vez de apagar."
        confirmarLabel="Sim, apagar"
        cancelarLabel="Não"
        onConfirmar={confirmarApagar}
        onCancelar={() => setApagando(null)}
      />
      <ConfirmDialog
        open={Boolean(emUso)}
        titulo={`${emUso?.codigo} está em uso e não pode ser apagado`}
        descricao={`${emUso?.motivo || "Há registros apontando para este depósito."} Deseja desativá-lo? Ele some das listas e o histórico continua íntegro.`}
        confirmarLabel="Sim, desativar"
        cancelarLabel="Não"
        tone="warning"
        onConfirmar={async () => { const d = emUso; setEmUso(null); await handleDesativar(d); }}
        onCancelar={() => setEmUso(null)}
      />
    </>
  );
}

export default Depositos;
